import { Pencil } from "lucide-react";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { buildMedicalRecordPatientViewModel } from "./medical-record-patient-view-model";

type PatientViewModel = ReturnType<typeof buildMedicalRecordPatientViewModel>;

interface MobilePatientChipProps {
  viewModel: PatientViewModel;
  phoneLabel: string;
  editLabel: string;
  onEdit: () => void;
}

export function MobilePatientChip({ viewModel, phoneLabel, editLabel, onEdit }: MobilePatientChipProps) {
  return (
    <section data-testid="medical-record-patient-chip" className="flex items-center gap-2 rounded-2xl border border-border/50 bg-card px-3.5 py-3 shadow-sm">
      <Avatar className="h-11 w-11 shrink-0">
        {viewModel.photoUrl ? <AvatarImage src={viewModel.photoUrl} alt={viewModel.petName} /> : null}
        <AvatarFallback className="bg-secondary text-primary">
          <CustomIcons name="paws" className="h-[22px] w-[22px]" />
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="truncate text-base font-bold text-primary">{viewModel.petName}</span>
          {viewModel.weightLabel ? <span className="text-xs font-semibold text-foreground">{viewModel.weightLabel}</span> : null}
        </div>
        {viewModel.detailLabels.length > 0 ? (
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{viewModel.detailLabels.join(" · ")}</p>
        ) : null}
        {viewModel.ownerName ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{viewModel.ownerName}</p> : null}
      </div>
      {viewModel.phoneNumber && viewModel.phoneIsWhatsApp ? (
        <WhatsAppPhoneLink
          phoneNumber={viewModel.phoneNumber}
          iconOnly
          ariaLabel={phoneLabel}
          iconLinkClassName="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
        />
      ) : null}
      <button
        type="button"
        aria-label={editLabel}
        onClick={onEdit}
        className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        <Pencil className="h-4 w-4" />
      </button>
    </section>
  );
}
